import { useCallback, useEffect, useRef, useState } from 'react'
import { getEntities, saveEntityStatus } from './manageEntitiesApi'

export const INBOUND = 'isInboundActive'
export const OUTBOUND = 'isOutboundActive'

/**
 * Owns the entity list and the per row save lifecycle.
 *
 * Toggles are applied optimistically so the switch moves under the cursor, then
 * rolled back if the request fails. The previous screen instead read the other
 * direction's checkbox out of the DOM with jQuery to build the request body;
 * here both flags come from state, which is also what makes the rollback exact.
 */
const useManageEntities = (connectorId, instancesId) => {
    const [entities, setEntities] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState('')
    const [savingId, setSavingId] = useState(null)
    const [status, setStatus] = useState(null)

    const isMounted = useRef(true)
    const loadedKey = useRef(null)

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    const load = useCallback(() => {
        if (!connectorId || !instancesId) {
            setIsLoading(false)
            return
        }

        setIsLoading(true)
        setLoadError('')
        setStatus(null)

        getEntities(connectorId, instancesId)
            .then((list) => {
                if (!isMounted.current) {
                    return
                }
                setEntities(list)
                setIsLoading(false)
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return
                }
                setLoadError('Unable to load the entities. Please try again.')
                setIsLoading(false)
                // eslint-disable-next-line no-console
                console.error('Manage entities load failed', error)
            })
    }, [connectorId, instancesId])

    useEffect(() => {
        // React 18 StrictMode runs effects twice in development. This ref survives
        // that simulated remount, so each connector/instance is fetched once.
        const key = `${connectorId}|${instancesId}`
        if (loadedKey.current === key) {
            return
        }
        loadedKey.current = key
        load()
    }, [connectorId, instancesId, load])

    const reload = useCallback(() => {
        loadedKey.current = `${connectorId}|${instancesId}`
        load()
    }, [connectorId, instancesId, load])

    const applyStatus = useCallback((entityId, changes) => {
        setEntities((current) => current.map(
            (entity) => (entity.id === entityId ? { ...entity, ...changes } : entity)
        ))
    }, [])

    const toggleEntity = useCallback((entity, direction, isActive) => {
        const next = {
            isInboundActive: entity.isInboundActive,
            isOutboundActive: entity.isOutboundActive,
            [direction]: isActive
        }

        setStatus(null)
        setSavingId(entity.id)
        applyStatus(entity.id, { [direction]: isActive })

        return saveEntityStatus(connectorId, instancesId, entity.id, next)
            .then((data) => {
                if (!isMounted.current) {
                    return
                }
                if (data.result === false) {
                    // Accepted but not stored - put the switch back where it was.
                    applyStatus(entity.id, { [direction]: !isActive })
                    setStatus({ result: false, message: data.message || 'No changes made.' })
                    return
                }
                setStatus({
                    result: true,
                    message: `${entity.displayName} updated successfully.`
                })
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return
                }
                applyStatus(entity.id, { [direction]: !isActive })
                setStatus({ result: false, message: `Unable to update ${entity.displayName}.` })
                // eslint-disable-next-line no-console
                console.error('Manage entities update failed', error)
            })
            .finally(() => {
                if (isMounted.current) {
                    setSavingId(null)
                }
            })
    }, [connectorId, instancesId, applyStatus])

    const dismissStatus = useCallback(() => setStatus(null), [])

    return {
        entities,
        isLoading,
        loadError,
        savingId,
        status,
        toggleEntity,
        dismissStatus,
        reload
    }
}

export default useManageEntities
